// src/components/UI.js
import React from 'react';

// Theme: deep navy background with cyan accents, matching the
// "Solve Today, Empower Tomorrow" login branding. Key names are kept
// the same as before (gold/goldLight/goldDim etc.) so every page that
// already references T.gold etc. picks up the new palette automatically.
export const T = {
  bg:'#050b18', surface:'#0b1f3a', card:'#0f2742', border:'#1e3a5f',
  gold:'#00d4ff', goldLight:'#5be3ff', goldDim:'#1e88e5',
  text:'#F0F6FF', muted:'#7e96b8', success:'#22C55E',
  warning:'#F59E0B', danger:'#EF4444', info:'#1e88e5',
};

export const STATUS_COLOR = {
  INQUIRY:'#3B82F6', ORDER_CREATED:'#8B5CF6', INVOICE_SENT:'#A855F7',
  PAYMENT_PENDING:'#F59E0B', PAID:'#22C55E', AWAITING_APPROVAL:'#EF4444',
  PRINTING:'#D4A017', PACKAGED:'#F97316', DISPATCHED:'#06B6D4',
  DELIVERED:'#22C55E', CLOSED:'#6B7280',
};

export const fmt = n => `KES ${Number(n || 0).toLocaleString()}`;

export function Badge({ status }) {
  const color = STATUS_COLOR[status] || '#666';
  return (
    <span style={{ background:`${color}22`, color, border:`1px solid ${color}44`, borderRadius:'6px', padding:'2px 9px', fontSize:'11px', fontWeight:700, display:'inline-block', whiteSpace:'nowrap' }}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export function Btn({ children, onClick, variant='gold', disabled, style={}, size='md' }) {
  const pad = size === 'sm' ? '5px 12px' : '9px 18px';
  const fs  = size === 'sm' ? '12px' : '13px';
  const base = { border:'none', borderRadius:'8px', padding:pad, fontWeight:700, fontSize:fs, cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.4:1, transition:'opacity 0.15s' };
  const vars = {
    gold:    { background:T.gold,    color:'#000' },
    outline: { background:'transparent', color:T.gold, border:`1px solid ${T.goldDim}` },
    danger:  { background:T.danger,  color:'#fff' },
    success: { background:T.success, color:'#fff' },
    info:    { background:T.info,    color:'#fff' },
    ghost:   { background:'transparent', color:T.muted, border:`1px solid ${T.border}` },
  };
  return <button onClick={disabled ? null : onClick} style={{ ...base, ...(vars[variant]||vars.gold), ...style }}>{children}</button>;
}

export function Card({ children, style={} }) {
  return <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:'12px', padding:'20px', marginBottom:'16px', ...style }}>{children}</div>;
}

export function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:'12px', padding:'18px', flex:1, minWidth:'130px' }}>
      <div style={{ fontSize:'11px', color:T.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>{label}</div>
      <div style={{ fontSize:'22px', fontWeight:800, color:color||T.gold }}>{value}</div>
      {sub && <div style={{ fontSize:'12px', color:T.muted, marginTop:'3px' }}>{sub}</div>}
    </div>
  );
}

export function Empty({ msg }) {
  return (
    <div style={{ textAlign:'center', padding:'48px 20px', color:T.muted }}>
      <div style={{ fontSize:'32px', marginBottom:'10px' }}>📭</div>
      <div style={{ fontSize:'14px' }}>{msg}</div>
    </div>
  );
}

export function Spinner() {
  return <div style={{ textAlign:'center', padding:'40px', color:T.muted, fontSize:'14px' }}>Loading...</div>;
}

export function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom:'20px' }}>
      <div style={{ fontSize:'20px', fontWeight:800, color:T.text }}>{children}</div>
      {sub && <div style={{ fontSize:'12px', color:T.muted, marginTop:'4px' }}>{sub}</div>}
    </div>
  );
}

export const TH = { textAlign:'left', padding:'10px 12px', fontSize:'11px', color:T.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:`1px solid ${T.border}` };
export const TD = { padding:'11px 12px', fontSize:'13px', borderBottom:`1px solid ${T.surface}` };
export const INP = { background:T.surface, border:`1px solid ${T.border}`, borderRadius:'8px', padding:'10px 14px', color:T.text, fontSize:'14px', width:'100%', outline:'none', boxSizing:'border-box' };
export const LBL = { fontSize:'11px', color:T.muted, marginBottom:'5px', display:'block', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' };

export function Modal({ children, onClose, title }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'#020812CC', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'20px' }}>
      <div style={{ background:T.card, border:`1px solid ${T.gold}44`, borderRadius:'14px', padding:'28px', width:'100%', maxWidth:'480px', maxHeight:'90vh', overflowY:'auto', position:'relative' }}>
        {title && <div style={{ fontSize:'16px', fontWeight:700, color:T.gold, marginBottom:'20px' }}>{title}</div>}
        {children}
        {onClose && <button onClick={onClose} style={{ position:'absolute', top:16, right:20, background:'none', border:'none', color:T.muted, fontSize:'20px', cursor:'pointer' }}>×</button>}
      </div>
    </div>
  );
}

export function InfoBox({ children, variant='info' }) {
  const colors = { info:T.info, warning:T.warning, danger:T.danger, success:T.success };
  const c = colors[variant] || T.info;
  return (
    <div style={{ background:`${c}11`, border:`1px solid ${c}33`, borderRadius:'8px', padding:'11px 14px', marginBottom:'16px', fontSize:'13px', color:c }}>
      {children}
    </div>
  );
}
